import { Card, CardHeader } from '../../../components/ui/Card';
import { QueryState } from '../../../components/ui/States';
import { Table, TTotalRow, THead, Th, Td, Tr } from '../../../components/ui/Table';
import { useSalesTeam } from '../../../hooks/useProjectManagement';
import { formatMoneyCompact } from '../../../utils/numberFormat';
import { getRep, type SalesRepSlug } from '../../ProjectManagement/team';
import type { TeamMemberSummary } from '../../../api/projectManagement';
import { secondaryKindsLine } from '../../ProjectManagement/Sales/dealKinds';
import { RepInitials } from '../shared';

/**
 * Pipeline → per rep: one row per rep — open, active and stale today, and new
 * business won since Attio went live (8 Jan 2026; Salesforce-era wins are not
 * credited). The stage split lives on the overview's stage chart.
 */
export function RepPipelineTable() {
  const query = useSalesTeam();

  return (
    <Card padding="md">
      <CardHeader title="Per rep" description="Open pipeline today · new business won since 8 Jan 2026" />
      <QueryState query={query} errorTitle="Couldn’t load the per-rep pipeline">
        {(data) => {
          const members = data.members.filter((m) => m.repSlug !== 'other' || m.openCount > 0 || m.wonCount > 0);
          return (
            <Table minWidth={640}>
              <THead>
                <tr>
                  <Th>Rep</Th>
                  <Th align="right">Open</Th>
                  <Th align="right">Active</Th>
                  <Th align="right" title={`No stage change and no account activity in ${data.staleAfterDays}+ days`}>Stale</Th>
                  <Th align="right">Won since 8 Jan</Th>
                </tr>
              </THead>
              <tbody>
                {members.map((m) => (
                  <RepRow key={m.repSlug} member={m} />
                ))}
              </tbody>
              <tfoot>
                <TotalRow members={members} />
              </tfoot>
            </Table>
          );
        }}
      </QueryState>
    </Card>
  );
}

/** Compact euros (the one money format on this page) with the deal count as a grey sub-line, so every cell reads the same way. */
function Money({ value, count }: { value: number; count: number }) {
  return (
    <span className="tabular-nums whitespace-nowrap">
      <span className="block text-text-primary">{formatMoneyCompact(value)}</span>
      <span className="block text-body-xs text-text-tertiary">
        {count} {count === 1 ? 'deal' : 'deals'}
      </span>
    </span>
  );
}

function TotalRow({ members }: { members: TeamMemberSummary[] }) {
  const sum = (pick: (m: TeamMemberSummary) => number) => members.reduce((n, m) => n + pick(m), 0);
  return (
    <TTotalRow>
      <Td>Total</Td>
      <Td align="right"><Money value={sum((m) => m.openValueEur)} count={sum((m) => m.openCount)} /></Td>
      <Td align="right"><Money value={sum((m) => m.activeValueEur)} count={sum((m) => m.activeCount)} /></Td>
      <Td align="right"><Money value={sum((m) => m.staleValueEur)} count={sum((m) => m.staleCount)} /></Td>
      <Td align="right">
        <Money value={sum((m) => m.wonByKind.new.valueEur)} count={sum((m) => m.wonByKind.new.count)} />
      </Td>
    </TTotalRow>
  );
}

function RepRow({ member }: { member: TeamMemberSummary }) {
  const rep = member.repSlug !== 'other' ? getRep(member.repSlug as SalesRepSlug) : null;
  const others = secondaryKindsLine(member.wonByKind);
  return (
    <Tr>
      <Td>
        <span className="flex items-center gap-2">
          <RepInitials rep={rep} />
          <span className="text-text-primary">{member.label}</span>
        </span>
      </Td>
      <Td align="right"><Money value={member.openValueEur} count={member.openCount} /></Td>
      <Td align="right"><Money value={member.activeValueEur} count={member.activeCount} /></Td>
      <Td align="right"><Money value={member.staleValueEur} count={member.staleCount} /></Td>
      <Td align="right">
        <span title={others || undefined}>
          <Money value={member.wonByKind.new.valueEur} count={member.wonByKind.new.count} />
        </span>
      </Td>
    </Tr>
  );
}
